import { ButtonType, ButtonWidget, DialogPositioningBehavior, DialogType } from '@tableau/widgets';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import React from 'react';

import { FormikTextBox } from '~commonInfra/components/FormikField';
import { ReactModalService } from '~commonInfra/dialogs/ReactModalService';
import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { CommonErrorCode } from '~commonInfra/utils/ErrorCodes';

import { Messages } from '../../codegen/Localize';
import { textFieldWidget } from '../../userSettings/styles/EmailAndDisplayNameFormStyles';
import { PersonalAccessToken } from '../models/PersonalAccessToken';
import { CreatePersonalAccessTokenResult } from '../serverApi/requests/CreatePersonalAccessTokenRequest';
import { IPersonalAccessToken } from '../serverApi/types/personalAccess/IPersonalAccessToken';
import { AuthenticationService } from '../services/AuthenticationService';
import { CreatePersonalAccessTokenDialogProps, GetDialogWidget, GetFooterContent } from './CreatePersonalAccessTokenDialog';

export interface CreatePersonalAccessTokensSectionProps {
  personalAccessTokens: IPersonalAccessToken[];
  refreshTokenList: () => void;
}

interface PersonalAccessTokensFormFields {
  tokenName: string;
}

function processCreateTokenError(result: CreatePersonalAccessTokenResult) {
  let errorMessage = Messages.errorUnknown();
  if (result && result.errors && result.errors.length) {
    switch (result.errors[0].code) {
      case CommonErrorCode.MaximumRefreshTokenReached:
        errorMessage = Messages.personalAccessTokensMaximumLimitReached();
    }
  }
  ToasterService.instance.pop({ type: ToastType.Error, text: errorMessage });
}

function openNewTokenDialog(params: {
  tokenName: string;
  refreshToken: string;
  onClose: () => void;
}) {
  const token: PersonalAccessToken = {
    tokenName: params.tokenName,
    tokenSecret: params.refreshToken,
  };

  ReactModalService.openCustom(closeAndDismiss => {
    const props: CreatePersonalAccessTokenDialogProps = {
      token: token,
      dismiss: closeAndDismiss.dismiss
    };
    const dialogProps = ReactModalService.getDefaultDialogProps({
      type: DialogType.NormalWithTitleBar,
      title: Messages.personalAccessTokenDialogTitle(),
      testId: 'personal-access-token-created-dialog',
      showCloseButton: true,
      positioningBehavior: DialogPositioningBehavior.Centered,
      dialogWidth: TabDialogWidth.width500px,
      footerContent: GetFooterContent(props)
    }, closeAndDismiss);

    return GetDialogWidget(dialogProps, token);
  }).result.finally(params.onClose);
}

export function CreatePersonalAccessTokensSection(props: CreatePersonalAccessTokensSectionProps) {

  function validatePersonalAccessTokenName(value: string): string | undefined {
    if (value && props.personalAccessTokens.find(x => x.clientId === value)) {
      return Messages.personalAccessTokenNameUsed();
    }
  }

  async function createPersonalAccessToken(tokenName: string, formik: FormikHelpers<any>): Promise<void> {
    const result = await AuthenticationService.createPersonalAccessToken(tokenName);
    if (result && result.refreshToken) {
      openNewTokenDialog({
        tokenName: tokenName,
        refreshToken: result.refreshToken,
        onClose: () => {
          formik.resetForm();
          props.refreshTokenList();
        }
      });
    } else {
      processCreateTokenError(result);
    }
  }

  return (
    <Formik
      initialValues={{ tokenName: '' }}
      onSubmit={(values: PersonalAccessTokensFormFields, formik: FormikHelpers<any>) => {
        createPersonalAccessToken(values.tokenName, formik);
      }}
      enableReinitialize={true}
    >
      {(formik: FormikProps<PersonalAccessTokensFormFields>) => (
        <form onSubmit={formik.handleSubmit} data-tb-test-id='my-form'>
          <table>
            <tbody>
              <tr>
                <td>
                  <FormikTextBox
                    name='tokenName'
                    testId='personal-access-token-name-input'
                    label={''}
                    validate={validatePersonalAccessTokenName}
                    className={textFieldWidget}
                    showValidationMessage={true}
                    required={true}
                    maxLength={50}
                    eagerValidation={true}
                    placeholder={Messages.personalAccessTokenName()}
                  />
                </td>
                <td>
                  <ButtonWidget
                    buttonType={ButtonType.Outline}
                    testId='personal-access-token-form-submit'
                    disabled={!formik.values.tokenName || !formik.isValid}
                    children={Messages.personalAccessTokenCreate()}
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </form>
      )}
    </Formik>
  );
}
